import { useState } from "react";
import { addMonths, format, isAfter, isBefore, startOfDay, startOfMonth } from "date-fns";
import { Calendar as CalendarIcon } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar, dateFilterYears } from "@/components/ui/calendar";

/**
 * Two independent month panels sharing one range selection.
 *
 * A single DayPicker with numberOfMonths={2} always shows two CONSECUTIVE months, so
 * changing either panel's month/year dropdown moved both. Two calendars let the left
 * panel go to where the range starts and the right panel to where it ends (e.g. Jan 2023
 * and Dec 2024) without paging. They open on the start and end months of `selected`.
 * Mount it fresh each time the picker opens (a popover does this), so it re-centres.
 */
export function RangeCalendar({ selected, onSelect }) {
  const [initial] = useState(() => {
    const { toYear } = dateFilterYears();
    const lastMonth = new Date(toYear, 11, 1);
    let left = startOfMonth(selected?.from || new Date());
    const end = selected?.to ? startOfMonth(selected.to) : null;
    let right = end && isAfter(end, left) ? end : addMonths(left, 1);
    if (isAfter(right, lastMonth)) {
      // Nothing to show after the last selectable month: show it with the month before.
      right = lastMonth;
      left = addMonths(lastMonth, -1);
    }
    return { left, right };
  });
  const [leftMonth, setLeftMonth] = useState(initial.left);
  const [rightMonth, setRightMonth] = useState(initial.right);

  const panel = (month, setMonth) => (
    <Calendar
      mode="range"
      captionLayout="dropdown-buttons"
      {...dateFilterYears()}
      month={month}
      onMonthChange={setMonth}
      selected={selected}
      onSelect={onSelect}
    />
  );

  return (
    <div className="flex flex-col sm:flex-row sm:divide-x divide-slate-100">
      {panel(leftMonth, setLeftMonth)}
      {panel(rightMonth, setRightMonth)}
    </div>
  );
}

/**
 * Date-range filter: a button showing the applied range, opening a two-panel calendar
 * with month/year dropdowns.
 *
 * Two clicks make a range: the first starts a new one, the second finishes it (in
 * either order; the same day twice is a one-day range). Nothing is applied until both
 * ends are picked, and then the popover closes. react-day-picker's own range logic
 * would instead EXTEND the range already showing, so the start could never move
 * forward and clicking the start day cleared the filter. Same behaviour as the owner
 * ledger's picker (AdminOwnerLedger.jsx).
 */
export default function DateRangeFilter({ value, onChange, className = "", align = "end" }) {
  const [open, setOpen] = useState(false);
  // What the calendar shows while a range is being picked.
  const [draft, setDraft] = useState(undefined);

  const onPick = (_range, day) => {
    if (!day) return;
    if (!draft?.from || draft.to) {
      setDraft({ from: day, to: undefined });
      return;
    }
    const backwards = isBefore(startOfDay(day), startOfDay(draft.from));
    const next = backwards ? { from: day, to: draft.from } : { from: draft.from, to: day };
    setDraft(next);
    onChange(next);
    setOpen(false);
  };

  return (
    <Popover open={open} onOpenChange={(o) => { setOpen(o); if (o) setDraft(value || undefined); }}>
      <PopoverTrigger asChild>
        <Button variant="outline" className={`justify-start text-left font-normal bg-white ${className}`}>
          <CalendarIcon className="w-4 h-4 mr-2 text-slate-400" />
          {value?.from ? (
            value.to ? (
              <>
                {format(value.from, "LLL dd, y")} - {format(value.to, "LLL dd, y")}
              </>
            ) : (
              format(value.from, "LLL dd, y")
            )
          ) : (
            <span className="text-slate-500">Pick a date range</span>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" align={align}>
        <RangeCalendar selected={draft || value} onSelect={onPick} />
      </PopoverContent>
    </Popover>
  );
}
